import { ChangeDetectorRef, Component, ComponentRef, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import {
  ListDetailsPanelComponent,
  SEARCH_BAR_ALIAS_NAME,
} from "../../../list-details-panel/list-details-panel.component";
import { SearchBarConfig, TableListRequest } from "../../../../models/table-list-request.interface";
import { PaginatedListDataResponse } from "../../../../models/paginated-list-data-response";
import { FinanceApiService } from "../../../../services/finance-a-p-iervice.service";
import { LoadingService } from "../../../../services/loading.service";
import { ManageInvoiceDetailsComponent } from "./manage-invoice-details/manage-invoice-details.component";
import { TosTableService } from "../../../../services/tos-table.service";
import { ActivatedRoute, Router } from "@angular/router";
import { InvoiceType, ViewMode } from "../../../../constants/finance-constants";
import { Subject, take, takeUntil } from "rxjs";
import { DateUtils } from "../../../../utils/date-utils";
import { ModalComponent } from "../../../modal/modal.component";
import { CreditNote, CreditType } from "../../../../models/credit-note";
import { FormsModule } from "@angular/forms";
import { ToasterService } from "../../../../services/toaster.service";
import InvoiceListingTableConfig from '../config/InvoiceListingTableConfig.json';
import { ResizeEvent } from "angular-resizable-element";

@Component({
  selector: "app-invoice-details",
  standalone: true,
  imports: [
    ListDetailsPanelComponent,
    FormsModule
  ],
  templateUrl: "./invoice-details.component.html",
  styleUrl: "./invoice-details.component.scss",
})
export class InvoiceDetailsComponent {
  selectedItems: any[] = [];
  selectedInvoiceId: string = "";
  sortColumn: string = "modifiedDate";
  sortDirection: "asc" | "desc" = "desc";
  tableListRequest: TableListRequest = {
    orderBy: {
      [this.sortColumn]: this.sortDirection
    },
    limit: 15,
    page: 0
  };
  tableListData: PaginatedListDataResponse = {
    totalCount: 0,
    page: 0,
    limit: 0,
    records: []
  };
  selectedInvoiceRecord: any = {};
  modalRef!: ComponentRef<ModalComponent>;
  cancelInvoiceDescription: string = "";
  searchBarConfig: SearchBarConfig = {
    filters: [
      { name: "SUPER_SEARCH", alias: `SUPER_SEARCH` }
    ],
    placeHolder: "Search by Invoice No or Customer Name"
  };
  filterConfig: any[] = [];

  private unsubscribe$ = new Subject<void>();

  @ViewChild("cancelInvoiceModalContent") cancelInvoiceModalContent!: TemplateRef<any>;

  constructor(private financeApiService: FinanceApiService,
              private loadingService: LoadingService,
              private tosTableService: TosTableService,
              private router: Router,
              private route: ActivatedRoute,
              private cdr: ChangeDetectorRef,
              private viewContainerRef: ViewContainerRef,
              private toasterService: ToasterService) {
    this.filterConfig = InvoiceListingTableConfig.filterConfig;
  }

  ngOnInit(): void {
    this.tosTableService.getTosTableConfig()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data) => {
          this.selectedInvoiceId = data.selectedItem ?? this.selectedInvoiceId;
          this.tableListRequest = data.tableListRequest ?? this.tableListRequest;
        }
      });
    this.getTableList();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  addDocumentInvoice() {
    this.router.navigate(["/manage-finance/invoices/add-new"], {
      queryParams: {
        type: InvoiceType.DOCUMENT,
        view: ViewMode.CARD
      }
    });
  }

  getTableList() {
    this.loadingService.show();
    this.financeApiService.listInvoices(this.tableListRequest).subscribe({
      next: (response: any) => {
        this.tableListData = this.processTableData(response.data);
        this.selectedInvoiceId = this.tableListData.records.find(record => record.id === this.selectedInvoiceId) ? this.selectedInvoiceId : response.data?.records[0]?.id;
        this.loadingService.hide();
      },
      error: (error) => {
        this.loadingService.hide();
      }
    });
  }

  handleCardOpened(event: any, appListDetailsPanel: ListDetailsPanelComponent) {
    if(this.selectedInvoiceRecord.id === event) return //already opened
    this.selectedInvoiceRecord = this.tableListData.records.find(record => record.id === event);
    this.cdr.detectChanges();
    const componentRefList = appListDetailsPanel.loadComponentsInRightPanel([{
      component: ManageInvoiceDetailsComponent,
      data: { invoiceRecord: this.selectedInvoiceRecord  }
    }]);
    const manageInvoiceDetailsComponentRef = componentRefList[0];
    manageInvoiceDetailsComponentRef.instance.actionClicked.subscribe((event: { actionName: string, record: any }) => {
      this.handleRightPanelAction(event);
    });

  }

  addMiscellaneousInvoice() {
    this.router.navigate(["/manage-finance/invoices/add-new"], {
      queryParams: {
        type: InvoiceType.MISCELLANEOUS,
        view: ViewMode.CARD
      }
    });
  }

  handleRightPanelAction(event: { actionName: string, record: any }) {
    const { actionName, record } = event;
    console.log("Action clicked:", actionName, record);

    //maintain state of this table before navigating to another component
    this.tosTableService.setTosTableConfig({
      selectedItem: record.id,
      tableListRequest: this.tableListRequest
    });

    switch (actionName) {
      case "record-payment":
        this.router.navigate([`manage-finance/invoices/record-payments/${record.id}`], {
          state: {
            recordPaymentInput: { invoiceIds: [record.id], redirectPath: "/manage-finance/invoices/details" }
          }
        });
        break;
      case "view":
        this.router.navigate([`manage-finance/invoices/view/${record.id}`], {
          queryParams: {
            type: record.invoiceType === "IMP" ? InvoiceType.DOCUMENT : InvoiceType.MISCELLANEOUS,
            view: ViewMode.CARD
          }
        });
        break;
      case "pdf-print":
        this.getInvoicePdf();
        break;
      case "credit-note":
        this.router.navigate([`manage-finance/credit-notes/add-new`], {
          state: {
            creditNoteInputs: { invoiceId: record.id, redirectPath: "/manage-finance/invoices/details" }
          }
        });
        break;
      case "cancel-invoice":
        this.modalRef = this.viewContainerRef.createComponent(ModalComponent);
        this.modalRef.instance.title = "Cancel Invoice";
        this.modalRef.instance.loadComponent(this.cancelInvoiceModalContent);

        this.modalRef.instance.close.subscribe((event: any) => {
          this.cancelInvoiceDescription = "";
          this.modalRef.destroy();
        });
        break;
      default:
        console.warn("Unknown action:", actionName);
    }
  }

  processTableData(tableListData: PaginatedListDataResponse) {
    return {
      ...tableListData,
      records: tableListData.records.map((record) => {
        return {
          ...record,
          invoiceDate: DateUtils.formatDate(record.invoiceDate, "dd/MM/yyyy"),
          amount: {
            ...record.amount
          }
        };
      })
    };
  }

  cancelInvoice() {
    this.loadingService.show();
    const selectedInvoiceRecord = this.tableListData.records.find(record => record.id === this.selectedInvoiceId);
    const invoiceCancellationRequest: CreditNote = {
      customerId: selectedInvoiceRecord?.customer.id,
      invoiceId: selectedInvoiceRecord?.id,
      creditNoteDate: new Date(),
      description: this.cancelInvoiceDescription,
      creditType: CreditType.INVOICE_CANCEL
    };

    this.financeApiService.createCreditNote(invoiceCancellationRequest).subscribe({
      next: (res: any) => {
        this.cancelInvoiceDescription = "";
        this.modalRef.destroy();
        this.loadingService.hide();
        this.toasterService.success("Invoice Cancelled Successfully");
      },
      error: (err: any) => {
        this.loadingService.hide();
        this.toasterService.error(err.error.message);
      }
    });
  }

  handleModalCancel() {
    this.cancelInvoiceDescription = "";
    this.modalRef.destroy();
  }

  goBack() {
    this.router.navigateByUrl(`/manage-finance/invoices`);
  }

  private getInvoicePdf() {
    this.financeApiService.printInvoicePdf(this.selectedInvoiceId).subscribe({
      next: (res: any) => {
        this.savePdf(res.data, `${this.selectedInvoiceRecord.invoiceNo}_tax_invoice.pdf`);
      },
      error: (err: any) => {
        this.toasterService.error(err.error.message);
      }
    })
  }

  private savePdf(base64Data: string, fileName: string): void {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    // Cleanup
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  }

}
